import axios from "axios";
import packsDataService from "features/Licensing/Packs/services/packsDataService";
import accountsDataService from "features/Accounts/services/accountsDataService";
import {
  type Accounts,
  type PacksListItem,
  allCustomerAccounts,
  isBsi,
} from "features/Library/Common/utils/performanceUtils";
import { SortingDirection } from "enums";
import { useQuery, useInfiniteQuery, type QueryFunctionContext } from "@tanstack/react-query";
import { useAddOnQuery } from "features/Reporting/Content/queries/useAddOnQuery";
import { type AddOn } from "features/Accounts/types";
import { useMemo, useState } from "react";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { type AccountType } from "../../interfaces/Account";

export const getSpCustomerList = async (accountId: number): Promise<Accounts[]> => {
  const { data } = await axios.get<Array<{ AccountId: number; AccountName: string }>>(
    "/api/reports/v1/accounts/customer-accounts",
    {
      params: { accountId },
    },
  );

  const asFilterList = data.map<Accounts>((account) => {
    return { value: account.AccountId, text: account.AccountName };
  });

  asFilterList.unshift(allCustomerAccounts);

  return asFilterList;
};

const fetchBsiLevelAccountsPage = async (
  skip: number,
  top: number,
  term?: string,
): Promise<{ items: Accounts[]; hasMore: boolean }> => {
  const { items } = await accountsDataService.getAccounts(skip, top, "name", SortingDirection.Ascending, term);

  const mapped = items.map((account) => ({ value: account.id, text: account.name }));
  return {
    items: mapped,
    hasMore: items.length === top,
  };
};

export const fetchAccounts = async ({
  queryKey,
}: QueryFunctionContext<[_name: string, accountType: AccountType, accountId: number]>): Promise<Accounts[]> => {
  if (queryKey[1] === "sp") {
    return getSpCustomerList(queryKey[2]);
  }
  return [];
};

const filterOutSvType = (accountType: AccountType): AccountType => {
  if (accountType === "sp") return "account";
  return accountType;
};

export interface ReportingAccountsListResult {
  items: Accounts[];
  loadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  isFetchingNext: boolean;
  error?: unknown;
  reset: () => void;
}

export interface ReportingPacksListResult {
  items: PacksListItem[];
  isLoading: boolean;
}

export interface ReportingListOptions {
  top?: number; // page size (default 100)
  term?: string; // server search term
  accumulate?: boolean; // accumulate pages (default true)
}

interface PageResult {
  items: Accounts[];
  hasMore: boolean;
  nextSkip?: number;
}

export const useReportingAccountsList = (
  accountType: AccountType,
  accountId: number,
  options: ReportingListOptions = {},
): ReportingAccountsListResult => {
  const { top = 100, term = "", accumulate = true } = options;
  const useSvReporting = useFeatureFlag(FeatureFlags.SvReportingFilter);
  const accountTypeForQuery = useSvReporting ? accountType : filterOutSvType(accountType);
  const [resetCounter, setResetCounter] = useState(0);

  // SP (service provider) path: single page (no pagination/search capability backend side here)
  const spQuery = useQuery<Accounts[]>({
    queryKey: ["accountsList", "v2", "sp", accountId, resetCounter],
    queryFn: () => getSpCustomerList(accountId),
    enabled: accountTypeForQuery === "sp",
  });

  const infiniteQuery = useInfiniteQuery<PageResult>({
    queryKey: ["accountsList", "v2", accountTypeForQuery, accountId, top, term, resetCounter],
    queryFn: async ({ pageParam = 0 }) => {
      const skip = (pageParam as number) ?? 0;
      const page = await fetchBsiLevelAccountsPage(skip, top, term);
      const firstPageNeedsSentinel = skip === 0 && term.length === 0;
      const itemsWithSentinel = firstPageNeedsSentinel ? [allCustomerAccounts, ...page.items] : page.items;
      return {
        items: itemsWithSentinel,
        hasMore: page.hasMore,
        nextSkip: page.hasMore ? skip + top : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextSkip,
    enabled: accountTypeForQuery !== "sp",
  });

  if (accountTypeForQuery === "sp") {
    return {
      items: spQuery.data ?? [],
      loadMore: () => {},
      hasMore: false,
      isLoading: spQuery.isLoading,
      isFetchingNext: false,
      error: spQuery.error,
      reset: () => setResetCounter((c: number) => c + 1),
    };
  }

  const pages = (infiniteQuery.data?.pages as PageResult[]) || [];
  const flattened = accumulate ? pages.flatMap((p) => p.items) : pages[pages.length - 1]?.items ?? [];

  const loadMore = () => {
    if (!infiniteQuery.hasNextPage || infiniteQuery.isFetchingNextPage) return;
    infiniteQuery.fetchNextPage();
  };

  const reset = () => setResetCounter((c: number) => c + 1);

  return {
    items: flattened,
    loadMore,
    hasMore: !!infiniteQuery.hasNextPage,
    isLoading: infiniteQuery.isLoading,
    isFetchingNext: infiniteQuery.isFetchingNextPage,
    error: infiniteQuery.error,
    reset,
  };
};

const fetchAccountPacksPage = async (
  accounts?: Accounts[] | number[] | string[],
  includeMyData?: boolean,
  type?: string,
): Promise<PacksListItem[]> => await packsDataService.getAccountPacksPage(accounts, includeMyData, type);

export const useReportingPacksList = (
  accounts?: Accounts[] | number[] | string[],
  includeMyData?: boolean,
  type?: string,
  includePacks?: boolean,
): ReportingPacksListResult => {
  const query = useQuery<PacksListItem[]>({
    queryKey: ["packsList", accounts, includeMyData, type],
    queryFn: async () => fetchAccountPacksPage(accounts, includeMyData, type),
    enabled: accounts !== undefined && includeMyData !== undefined && type !== undefined && includePacks !== undefined,
  });

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
  };
};

const hasMarketplaceAddOn = (addOns: AddOn[]): boolean => {
  const marketPlace = addOns.find((addOn) => addOn.name?.match(/marketplace/i));
  if (!marketPlace) return false;

  return marketPlace.isEnabled;
};

// Basing off of https://brainstorm.atlassian.net/wiki/spaces/BD/pages/4828921861/Addons+Data+relations
export const useReportingAccountType = (
  accountId: number,
  supportsSPType: boolean = false,
  publisherId?: number,
): AccountType | undefined => {
  const addOnQuery = useAddOnQuery(accountId);
  const svReportingFilter = useFeatureFlag(FeatureFlags.SvReportingFilter);

  const isSp = useMemo(() => {
    const hasAddOnData = !!addOnQuery.data;
    const hasMarketplace = addOnQuery.data ? hasMarketplaceAddOn(addOnQuery.data) : false;
    const hasSvFlag = !!svReportingFilter;

    const base = hasAddOnData && hasMarketplace && hasSvFlag && supportsSPType;

    if (publisherId === -1 || svReportingFilter === undefined || addOnQuery.data === undefined) return undefined;
    return base && (publisherId === undefined || publisherId === accountId);
  }, [addOnQuery.data, svReportingFilter, supportsSPType, publisherId, accountId]);

  // Return early for BSI accounts (doesn't depend on async data)
  if (isBsi(accountId)) return "bsi";

  if (isSp === undefined || addOnQuery.isLoading) {
    return undefined; // Don't return anything until data is loaded
  }
  if (isSp) {
    return "sp";
  }
  return "account";
};
